import React, { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../Context/GlobalContext";
import Chart from "react-apexcharts";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Info, Link2, MapPin, Share2 } from "lucide-react";
import { images } from "../Assets/images";
import { normalizeScanDetails } from "./scanDetails/scanNormalizer";
import { statusIconMap, getWellnessLabel } from "../utils";
import HealthDetailDrawer from "../Components/HealthDetailDrawer";
import storage from "../Assets/js/Global";
import "../Assets/css/Dashboard.css";

const COLORS = {
  low: "#f30606",
  medium: "#facc15",
  high: "#22c55e",
  gray: "#9e9a9a",
};

const TYPE_TO_FILTER = {
  cardiovascular: "CV",
  cv: "CV",
  cardio: "CV",
  cardio_vascular: "CV",
  heart: "CV",
  metabolic: "Metabolic",
  metabolism: "Metabolic",
  immune: "Immune",
  immunity: "Immune",
  sugar: "Sugar",
  blood_sugar: "Sugar",
  diabetes: "Sugar",
  stress: "Stress",
};

const mapTypeToFilter = (key) => {
  const norm = (key || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return TYPE_TO_FILTER[norm] || "all";
};

const Dashboard2 = () => {
  const {
    translatedLabels,
    logoUrl,
    getScanDetails,
    scanDetails,
    products,
    dynamicTypes,
    redirection,
    is4Life,
    // addUserToChat,
  } = useContext(GlobalContext);

  const screenLabels = translatedLabels?.common;
  const navigate = useNavigate();

  const normalizedScanDetails = useMemo(
    () => normalizeScanDetails(scanDetails, screenLabels),
    [scanDetails, screenLabels],
  );
  let chartVal = normalizedScanDetails[0].metrics[0].value;
  const [selectedType, setSelectedType] = useState(null);
  const [selectedMetricData, setSelectedMetricData] = useState(null);
  const [showIconAndImage, setShowIconAndImage] = useState(true);
  const [showCardDetail, setShowCardDetail] = useState(false);
  const [activeTab, setActiveTab] = useState(null);

  // useEffect(() => {
  //   addUserToChat();
  // }, []);

  useEffect(() => {
    if (dynamicTypes && Object.keys(dynamicTypes).length > 0) {
      // setSelectedType(Object.keys(dynamicTypes)[0]);
      setSelectedType(null);
    }
  }, [dynamicTypes]);

  useEffect(() => {
    getScanDetails();
  }, [getScanDetails]);

  // const openDrawer = (metricData, withIcon = false, tab = null) => {
  //   if (tab) setActiveTab(tab);
  //   setSelectedMetricData(metricData);
  //   setShowIconAndImage(withIcon);
  //   setShowCardDetail(true);

  //   window.history.pushState({ modalOpen: true }, "");
  // };
  // const closeDrawer = () => {
  //   setShowCardDetail(false);
  //   if (window.history.state?.modalOpen) {
  //     window.history.back();
  //   }
  // };

  const openDrawer = (metricData, withIcon = false, tab = null) => {
    if (tab) setActiveTab(tab);
    setSelectedMetricData(metricData);
    setShowIconAndImage(withIcon);
    setShowCardDetail(true);

    window.history.pushState({ modalOpen: true }, "");
  };

  const closeDrawer = () => {
    setShowCardDetail(false);
    setSelectedMetricData(null);
    setShowIconAndImage(false);
    setActiveTab(null);

    if (window.history.state?.modalOpen) {
      window.history.back();
    }
  };

  const wellnessValue =
    scanDetails?.body?.average_score ??
    0;


  const series = useMemo(() => {
    const val = Number(normalizedScanDetails[0].metrics[0].value) || 0;
    return [Math.max(0, Math.min(10, val)) * 10];
  }, [normalizedScanDetails[0].metrics[0].value]);

  const options = {
    chart: { type: "radialBar", offsetY: 0, sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: -150,
        endAngle: 150,
        hollow: { size: "85%", background: "transparent" },
        track: { background: "#f0f0f0", strokeWidth: "100%", margin: 0 },
        dataLabels: {
          name: { offsetY: 30, show: true, color: "#574f8a", fontSize: "16px" },
          value: {
            offsetY: -10,
            fontSize: "35px",
            color: "#574f8a",
            fontWeight: 600,
            show: true,
            formatter: () => `${wellnessValue}/10`,
          },
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.4,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 25, 50, 75, 100],
        colorStops: [
          { offset: 0, color: "#00d4ff", opacity: 1 },
          { offset: 25, color: "#7c3aed", opacity: 1 },
          { offset: 50, color: "#ec4899", opacity: 1 },
          { offset: 75, color: "#f97316", opacity: 1 },
          { offset: 100, color: "#ef4444", opacity: 1 },
        ],
      },
    },
    stroke: { lineCap: "round" },
    labels: [getWellnessLabel(wellnessValue, screenLabels)],
  };

  const getScoreColor = (cv_color) => {
    if (cv_color === "red") return COLORS.low;
    if (cv_color === "yellow") return COLORS.medium;
    if (cv_color === "green") return COLORS.high;
    return COLORS.gray;
  };

  let redirecturl = redirection?.productLink;
  let shareMsg = redirection?.productShare;

  const handleShare = async () => {
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(`share|${shareMsg || ""}`);
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ text: shareMsg || "", url: redirecturl || "" });
      } catch (e) { /* user cancelled */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(shareMsg || redirecturl || "");
      window.showToast?.("success", "Copied to clipboard");
    } catch {
      window.showToast?.("error", "Share not supported");
    }
  };

  const handleOpenLink = () => {
    let url = redirecturl;

    if (!url || url.startsWith("{")) {
      window.showToast("error", "Link not available or unauthorized");
      return;
    }

    if (!url.startsWith("http")) {
      url = url;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getPosition = (percent) => {
    if (percent === null || percent === undefined || isNaN(percent)) return 0;
    return Math.min(100, Math.max(0, percent));
  };

  const getBiomarkerColor = (percent, min, max) => {
    const p = Number(percent);
    const minVal = Number(min);
    const maxVal = Number(max);

    if (isNaN(p) || isNaN(minVal) || isNaN(maxVal)) return COLORS.gray;

    if (p < minVal) return COLORS.low;
    if (p >= minVal && p <= maxVal) return COLORS.high;

    return COLORS.low;
  };

  const getGradient = (min, max) => {
    const buffer = 6;

    return `linear-gradient(
    to right,
    ${COLORS.low} 0%,
    ${COLORS.low} ${min - buffer}%,

    ${COLORS.medium} ${min}%,

    ${COLORS.high} ${min + buffer}%,
    ${COLORS.high} ${max - buffer}%,

    ${COLORS.medium} ${max}%,

    ${COLORS.low} ${max + buffer}%,
    ${COLORS.low} 100%
  )`;
  };
  return (
    <div className="content-part">
      {!dynamicTypes || Object.keys(dynamicTypes).length === 0 ? (
        <div className="page-centered-card d-flex flex-column align-items-center justify-content-center text-center">
          <div className="mb-3">
            <img
              src={logoUrl || images.appLogo}
              alt="app-logo"
              style={{
                width: "120px",
                height: "120px",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = images.appLogo;
              }}
            />
          </div>

          <h4 className="mb-2 fw-bold">
            {screenLabels?.no_health_data?.label_value ||
              "No Health Data Available"}
          </h4>
          <p className="text-muted" style={{ maxWidth: "400px" }}>
            {screenLabels?.start_scan_inst?.label_value ||
              "Start your health journey by scanning now! A quick scan helps you track vital biomarkers, understand your wellness score, and get personalized insights to improve your lifestyle."}
          </p>
          <div className="mt-3 small text-muted">
            <p>
              ✔
              {screenLabels?.scan_det_1?.label_value ||
                "Quick and non-invasive"}
            </p>
            <p>
              ✔{" "}
              {screenLabels?.scan_det_2?.label_value ||
                "Get detailed wellness insights"}
            </p>
            <p>
              ✔{" "}
              {screenLabels?.scan_det_3?.label_value ||
                "Track your progress over time"}
            </p>
          </div>
          <button
            className="btn btn-primary mt-3 px-4"
            onClick={() => navigate("/start-scan")}
          >
            {screenLabels?.start_scan?.label_value || "Start Scan"}
          </button>
        </div>
      ) : (
        <>
          <div className={`dash-hero-card`}>
            <div className="dashboard-logo">
              <img
                src={logoUrl || images.appLogo}
                alt="app-logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = images.appLogo;
                }}
              />
            </div>

            <div className="ml-2">
              {chartVal && (
                <>
                  <div className="mb-2 d-flex justify-content-center">
                    <Chart
                      key={chartVal}
                      options={options}
                      series={series}
                      type="radialBar"
                      className="chart wellness-chart"
                    />
                    <div style={{ top: "15px", left: "135px" }}></div>
                  </div>
                  <div className="d-flex justify-content-center align-items-center flex-column">
                    <p
                      className="wellness-title mb-0 d-flex align-items-center gap-1 fs-15px"
                    // onClick={() => {
                    //   openDrawer(
                    //     {
                    //       key: "wellness",
                    //       value: normalizedScanDetails[0].metrics[0].value,
                    //       unit: "/10",
                    //       status: normalizedScanDetails?.wellness?.status,
                    //       comment: normalizedScanDetails?.wellness?.comment,
                    //       avg: normalizedScanDetails?.wellness?.avg ?? null,
                    //       icon: statusIconMap[
                    //         normalizedScanDetails?.wellness?.status
                    //       ],
                    //     },
                    //     false,
                    //     "all",
                    //   );
                    // }}
                    >
                      {screenLabels?.wellness_score?.label_value ||
                        "Wellness Score"}{" "}
                      {/* <Info className="text-primary" size={18} /> */}
                    </p>
                    <p className="wellness-desc mb-0">
                      {normalizedScanDetails?.wellness?.comment}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-6">
              <div style={{ marginBottom: 24 }}>
                <h6 className="section-title-modern mb-3">
                  {screenLabels?.health_overview?.label_value ||
                    "Health Overview"}
                </h6>

                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {Object.entries(dynamicTypes).map(([key, t]) => {
                    const percent = (t.score / 10) * 100;
                    const dynamicColor = getScoreColor(t.color);
                    const isOpen = selectedType === key;

                    return (
                      <div
                        key={key}
                        className="health-card"
                        style={{
                          "--percent": `${percent}%`,
                          "--accent-color": dynamicColor,
                          "--bg-gradient": `linear-gradient(to right, ${dynamicColor}, ${dynamicColor}dd)`,
                        }}
                      >
                        <div
                          onClick={() => setSelectedType(isOpen ? null : key)}
                          className="health-card-header"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <span className="health-title">{t.label}</span>
                          </div>

                          <div className="d-flex align-items-center gap-3">
                            <span className="health-score mb-0">
                              {t.score}/10
                            </span>
                            <ChevronDown
                              size={20}
                              className={`chevron text-dark ${isOpen ? "rotate" : ""}`}
                            />
                          </div>
                        </div>

                        {isOpen && (
                          <div style={{ background: "#fff" }}>
                            <div style={{ padding: "0 16px 5px" }}>
                              <p className="fs-13px fw-semibold text-muted mb-2 mt-2">
                                {screenLabels?.top_contributing_factors
                                  ?.label_value || "Top Contributing Factors"}
                              </p>

                              {t.biomarkers?.map((bm, index) => (
                                // <div key={index} style={{ marginBottom: 16 }}>
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: 16,
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    openDrawer(
                                      {
                                        key: bm.rawKey,
                                        percent: bm.percent ?? "-",
                                        value: bm.value ?? "-",
                                        unit: bm.unit || "",
                                        avg: null,
                                        comment: bm.name,
                                        status:
                                          bm.percent < bm.minPercent
                                            ? "low"
                                            : bm.percent > bm.maxPercent
                                              ? "high"
                                              : "normal",
                                        icon: statusIconMap[
                                          bm.percent < bm.minPercent
                                            ? "low"
                                            : bm.percent > bm.maxPercent
                                              ? "high"
                                              : "normal"
                                        ],
                                        normalValue: bm.normalValue,
                                        avgValue: bm.avgValue,
                                        shadowColor: bm.shadowColor,
                                        source: is4Life
                                          ? "biomarker_dashboard"
                                          : "",
                                        parentType: key,
                                      },
                                      true,
                                      bm.name?.toLowerCase().replace(/\s/g, ""),
                                    )
                                  }
                                >
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      marginBottom: 6,
                                      color: "#2e2e30",
                                    }}
                                  >
                                    {bm.name}
                                  </div>

                                  <div
                                    className="mt-3"
                                    style={{
                                      position: "relative",
                                      height: 8,
                                      borderRadius: 999,
                                      background: getGradient(
                                        bm.minPercent,
                                        bm.maxPercent,
                                      ),
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: -17,
                                        left: `${getPosition(bm.percent)}%`,
                                        transform: "translateX(-50%)",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                      }}
                                    >
                                      <MapPin
                                        size={12}
                                        fill={getBiomarkerColor(
                                          bm.percent,
                                          bm.minPercent,
                                          bm.maxPercent,
                                        )}
                                        stroke={getBiomarkerColor(
                                          bm.percent,
                                          bm.minPercent,
                                          bm.maxPercent,
                                        )}
                                      />
                                    </div>

                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: `${getPosition(bm.percent)}%`,
                                        transform: "translate(-50%, -50%)",
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        background: "#fff",
                                        border: `2px solid ${getBiomarkerColor(bm.percent, bm.minPercent, bm.maxPercent)}`,
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                                      }}
                                    />
                                  </div>

                                  {/* <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginTop: 4,
                                  fontSize: 11,
                                  fontWeight: 500,
                                }}
                              >
                                <span>Low</span>
                                <span>Normal</span>
                                <span>High</span>
                              </div> */}
                                </div>
                              ))}
                            </div>

                            <div style={{ padding: "0 16px 16px" }}>
                              <p className="fs-14px mb-2">{t.desc}</p>

                              <div style={{ textAlign: "right" }}>
                                <div className="d-flex justify-content-end">
                                  <button
                                    className="btn btn-primary"
                                    onClick={() =>
                                      navigate("/show-all-result-2", {
                                        state: {
                                          selectedFilter: mapTypeToFilter(key),
                                        },
                                      })
                                    }
                                  >
                                    {screenLabels?.view_all_results
                                      ?.label_value || "View all Results"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <h6 className="section-title-modern mb-3">
                {screenLabels?.recommended_products_title?.label_value ||
                  "Based on your results, here are your Recommended Products"}
              </h6>

              <div className="row g-3 mb-3">
                {products.top4.map((product) => (
                  <div className="col-6 pt-0" key={product.id}>
                    <div
                      // to={`/recommended-product?scanID=${products?.scanId}`}
                      className="nav-link mb-0 overflow-hidden product-card"
                    >
                      <div className="rp-image">
                        <img
                          src={product.image}
                          alt="product"
                          className="img-fluid"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = images.placeHolderImg;
                          }}
                        />
                      </div>

                      <div className="rp-body p-2 flex-grow-1 d-flex flex-column">
                        <h6 className="mb-1 recommended-product-title fw-semibold">
                          {product.name}
                        </h6>

                        {/* <p className="mb-2 recommended-product-description text-muted flex-grow-1">
                            {product.description}
                          </p> */}

                        {/* <div className="text-center mb-2">
                      <span className="text-primary">View More</span>
                    </div> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex gap-2 justify-content-center mb-4">
                <button
                  className="btn btn-primary px-4 py-2 rounded-pill"
                  onClick={() => handleShare()}
                >
                  <Share2 size={19} />{" "}
                  {screenLabels?.share?.label_value || "Share"}
                </button>
                <button
                  className="btn btn-primary px-4 py-2 rounded-pill"
                  onClick={() => handleOpenLink()}
                >
                  <Link2 size={22} />{" "}
                  {screenLabels?.open_link?.label_value || "Open Link"}
                </button>
              </div>
            </div>
          </div>

          {products.remaining.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="section-title-modern mb-0">
                  More Recommended Products
                </h6>
                {/* <button
              className="btn btn-outline-primary btn-sm rounded-pill"
              onClick={() =>
                navigate(`/recommended-product?scanID=${products?.scanId}`)
              }
              style={{ fontSize: 12 }}
            >
              View All
            </button> */}
              </div>

              <div
                className="card"
                style={{ borderRadius: 12, padding: "16px 20px" }}
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {products.remaining.map((p) => (
                    <div key={p.id} className="more-products-badge">
                      {p.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <HealthDetailDrawer
        show={showCardDetail}
        onClose={closeDrawer}
        activeTab={activeTab}
        showIconAndImage={showIconAndImage}
        metricData={selectedMetricData}
      />
    </div>
  );
};

export default Dashboard2;
